import { Building2, Check, KeyRound, LogOut, Mail, MessageCircle, Pencil, RefreshCw, Search, Send, Smartphone, Trash2, UserPlus, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../../api/client';

const WECOM_WEBHOOK_URL = `${window.location.origin}/api/webhook/wecom/kf`;
const WECOM_CONTACT_URL = process.env.REACT_APP_WECOM_CONTACT_URL || '';

const CHANNEL_TABS = [
  { id: 'telegram', label: 'TG', icon: Send },
  { id: 'email', label: '邮箱', icon: Mail },
  { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
  { id: 'wecom', label: '企业微信', icon: MessageCircle },
  { id: 'teams', label: 'Teams', icon: Building2 },
];

const EMPTY_EMAIL_FORM = {
  enabled: false,
  host: '',
  port: '587',
  username: '',
  password: '',
  from: '',
  auth: true,
  startTls: true,
  defaultSubject: 'Seeme message',
  inboundEnabled: false,
  imapHost: '',
  imapPort: '993',
  imapUsername: '',
  imapPassword: '',
  imapProtocol: 'imaps',
  imapFolder: 'INBOX',
};

const EMPTY_EMAIL_TEMPLATE_FORM = {
  name: '',
  host: '',
  port: '587',
  auth: true,
  startTls: true,
  imapHost: '',
  imapPort: '993',
  imapProtocol: 'imaps',
  imapFolder: 'INBOX',
};

const EMAIL_WORKSPACE_TABS = [
  { id: 'settings', label: '当前邮箱配置' },
  { id: 'templates', label: '模板管理' },
];

const hasText = value => String(value || '').trim().length > 0;
const looksLikeEmail = value => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

const templateFormFromTemplate = template => ({
  name: template?.name || '',
  host: template?.host || '',
  port: String(template?.port || 587),
  auth: Boolean(template?.auth),
  startTls: Boolean(template?.startTls),
  imapHost: template?.imapHost || '',
  imapPort: String(template?.imapPort || 993),
  imapProtocol: template?.imapProtocol || 'imaps',
  imapFolder: template?.imapFolder || 'INBOX',
});

export const validateEmailTemplateForm = (form) => {
  if (!hasText(form.name)) {
    return '模板名称不能为空';
  }
  if (!hasText(form.host) || !Number(form.port)) {
    return '模板必须填写 SMTP Host 和端口';
  }
  if (!hasText(form.imapHost) || !Number(form.imapPort)) {
    return '模板必须填写 IMAP Host 和端口';
  }
  return '';
};

export const detectEmailProviderTemplate = (form = {}, templates = []) => {
  const normalizedHost = String(form.host || '').trim().toLowerCase();
  const normalizedPort = String(form.port || '').trim();
  const normalizedImapHost = String(form.imapHost || '').trim().toLowerCase();
  const normalizedImapPort = String(form.imapPort || '').trim();
  const normalizedProtocol = String(form.imapProtocol || '').trim().toLowerCase();

  const matched = templates.find(template =>
    normalizedHost === String(template.host || '').trim().toLowerCase()
    && normalizedPort === String(template.port || '')
    && normalizedImapHost === String(template.imapHost || '').trim().toLowerCase()
    && normalizedImapPort === String(template.imapPort || '')
    && Boolean(form.auth) === template.auth
    && Boolean(form.startTls) === template.startTls
    && normalizedProtocol === String(template.imapProtocol || '').trim().toLowerCase()
  );

  return matched?.id || 'custom';
};

export const applyEmailProviderTemplate = (form, template) => {
  if (!template) return form;
  return {
    ...form,
    host: template.host || '',
    port: String(template.port || 587),
    auth: Boolean(template.auth),
    startTls: Boolean(template.startTls),
    imapHost: template.imapHost || '',
    imapPort: String(template.imapPort || 993),
    imapProtocol: template.imapProtocol || 'imaps',
    imapFolder: template.imapFolder || 'INBOX',
  };
};

export const validateEmailSettingsForm = (form, currentSettings = {}) => {
  const smtpPort = Number(form.port);
  const imapPort = Number(form.imapPort);
  if (form.enabled && (!hasText(form.host) || !smtpPort || !looksLikeEmail(form.username) || !looksLikeEmail(form.from))) {
    return '启用发信时必须填写 SMTP Host、端口、完整邮箱用户名和 From 地址';
  }
  if (form.enabled && !hasText(form.password) && !currentSettings?.passwordConfigured) {
    return '启用发信时必须填写 SMTP 密码；保存后密码会以星号显示';
  }
  if (form.inboundEnabled && (!hasText(form.imapHost) || !imapPort || !looksLikeEmail(form.imapUsername))) {
    return '启用收信时必须填写 IMAP Host、端口和完整邮箱用户名';
  }
  if (form.inboundEnabled && !hasText(form.imapPassword) && !currentSettings?.imapPasswordConfigured) {
    return '启用收信时必须填写 IMAP 密码；保存后密码会以星号显示';
  }
  return '';
};

export const passwordStateText = (configured, label) => (
  configured ? `${label}已保存，留空保存时不会修改` : `${label}未保存，启用前必须填写`
);

export default function ChannelSettingsView() {
  const [activeTab, setActiveTab] = useState('telegram');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const channel = params.get('channel');
    if (CHANNEL_TABS.some(tab => tab.id === channel)) {
      setActiveTab(channel);
    }
  }, []);

  return (
    <main className="settings-view channel-settings-view">
      <div className="settings-header">
        <div>
          <div className="settings-title">渠道配置</div>
          <div className="settings-subtitle">统一管理 TG、邮箱、WhatsApp、企业微信和 Teams 接入</div>
        </div>
      </div>

      <div className="channel-settings-layout">
        <aside className="channel-tab-list">
          {CHANNEL_TABS.map(tab => (
            <button
              key={tab.id}
              type="button"
              className={`channel-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <tab.icon size={16} />
              <span>{tab.label}</span>
            </button>
          ))}
        </aside>

        <section className="channel-tab-panel">
          {activeTab === 'telegram' && <TelegramChannel />}
          {activeTab === 'email' && <EmailChannel />}
          {activeTab === 'whatsapp' && <WhatsAppChannel />}
          {activeTab === 'wecom' && <WeComChannel />}
          {activeTab === 'teams' && <TeamsChannel />}
        </section>
      </div>
    </main>
  );
}

function WeComChannel() {
  const [status, setStatus] = useState();
  const [metrics, setMetrics] = useState();
  const [accounts, setAccounts] = useState([]);
  const [members, setMembers] = useState([]);
  const [externalContactWays, setExternalContactWays] = useState([]);
  const [settings, setSettings] = useState();
  const [users, setUsers] = useState([]);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [manualToken, setManualToken] = useState('');
  const [manualOpenKfid, setManualOpenKfid] = useState('');
  const [contactWayOpenKfid, setContactWayOpenKfid] = useState('');
  const [contactWayScene, setContactWayScene] = useState('erp');
  const [contactWayUrl, setContactWayUrl] = useState('');
  const [externalContactUserid, setExternalContactUserid] = useState('');
  const [externalContactScene, setExternalContactScene] = useState('website');
  const [externalContactRemark, setExternalContactRemark] = useState('');
  const [externalContactQrCode, setExternalContactQrCode] = useState('');

  useEffect(() => {
    refreshWeCom();
  }, []);

  const refreshWeCom = async () => {
    setError('');
    try {
      const [nextStatus, nextMetrics, nextAccounts, nextMembers, nextExternalContactWays, nextSettings, nextUsers] = await Promise.all([
        api.getWeComStatus(),
        api.getWeComMetrics().catch(() => undefined),
        api.listWeComAccounts().catch(() => []),
        api.listWeComMembers().catch(() => []),
        api.listWeComExternalContactWays().catch(() => []),
        api.getWeComSettings().catch(() => undefined),
        api.listPermissionUsers().catch(() => []),
      ]);
      setStatus(nextStatus);
      setMetrics(nextMetrics);
      setAccounts(nextAccounts || []);
      setMembers(nextMembers || []);
      setExternalContactWays(nextExternalContactWays || []);
      setSettings(nextSettings);
      setUsers((nextUsers || []).filter(user => user.status === '启用'));
      setContactWayOpenKfid(current => current || nextAccounts?.[0]?.openKfid || '');
      setExternalContactUserid(current => current || nextMembers?.find(member => member.erpUserId)?.userid || nextMembers?.[0]?.userid || '');
    } catch (err) {
      setError(err.message || '企业微信状态读取失败');
    }
  };

  const copyText = async (text, message) => {
    await navigator.clipboard?.writeText(text);
    setNotice(message);
  };

  const triggerSync = async () => {
    if (!manualToken.trim()) {
      setError('手动同步需要填写企业微信回调事件里的 Token');
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await api.triggerWeComSync({
        token: manualToken.trim(),
        openKfid: manualOpenKfid.trim() || undefined,
      });
      setNotice('已提交企业微信手动同步任务');
      setManualToken('');
    } catch (err) {
      setError(err.message || '企业微信手动同步失败');
    } finally {
      setBusy(false);
    }
  };

  const syncAccounts = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const synced = await api.syncWeComAccounts();
      setAccounts(synced || []);
      setContactWayOpenKfid(current => current || synced?.[0]?.openKfid || '');
      setNotice('客服帐号已同步');
    } catch (err) {
      setError(err.message || '客服帐号同步失败');
    } finally {
      setBusy(false);
    }
  };

  const syncMembers = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const synced = await api.syncWeComMembers();
      setMembers(synced || []);
      setNotice('企业微信成员已同步');
    } catch (err) {
      setError(err.message || '企业微信成员同步失败，请确认自建应用有通讯录可见范围和接口权限');
    } finally {
      setBusy(false);
    }
  };

  const bindMember = async (userid, erpUserId) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const saved = await api.bindWeComMember(userid, erpUserId);
      setMembers(current => current.map(member => member.userid === saved.userid ? saved : member));
      setNotice('企业微信成员绑定已保存');
    } catch (err) {
      setError(err.message || '企业微信成员绑定失败');
    } finally {
      setBusy(false);
    }
  };

  const createExternalContactWay = async () => {
    if (!externalContactUserid) {
      setError('请先选择企业微信成员');
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    setExternalContactQrCode('');
    try {
      const result = await api.createWeComExternalContactWay({
        userid: externalContactUserid,
        scene: externalContactScene.trim() || 'website',
        remark: externalContactRemark.trim() || undefined,
        skipVerify: true,
      });
      setExternalContactQrCode(result.qrCode);
      setNotice('员工联系二维码已生成');
      const ways = await api.listWeComExternalContactWays().catch(() => []);
      setExternalContactWays(ways || []);
    } catch (err) {
      setError(err.message || '员工联系二维码生成失败，请确认应用已开通客户联系权限');
    } finally {
      setBusy(false);
    }
  };

  const saveGlobalAssignee = async (assigneeId) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const saved = await api.saveWeComSettings({ globalDefaultAssigneeId: assigneeId });
      setSettings(saved);
      setNotice('企业微信默认负责人已保存');
    } catch (err) {
      setError(err.message || '保存默认负责人失败');
    } finally {
      setBusy(false);
    }
  };

  const saveAccountAssignee = async (openKfid, assigneeId) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const saved = await api.saveWeComAccountAssignee(openKfid, assigneeId);
      setAccounts(current => current.map(account => account.openKfid === saved.openKfid ? saved : account));
      setNotice('客服帐号默认负责人已保存');
    } catch (err) {
      setError(err.message || '保存客服帐号负责人失败');
    } finally {
      setBusy(false);
    }
  };

  const createContactWay = async () => {
    if (!contactWayOpenKfid) {
      setError('请先选择客服帐号');
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    setContactWayUrl('');
    try {
      const result = await api.createWeComContactWay({
        openKfid: contactWayOpenKfid,
        scene: contactWayScene.trim() || 'erp',
      });
      setContactWayUrl(result.url);
      setNotice('客服链接已生成');
    } catch (err) {
      setError(err.message || '客服链接生成失败');
    } finally {
      setBusy(false);
    }
  };

  const userName = (userId) => users.find(user => String(user.id) === String(userId))?.displayName || '';

  return (
    <div className="channel-panel-stack">
      <div className="channel-header-row">
        <div>
          <div className="channel-heading">
            <MessageCircle size={18} />
            <span>企业微信</span>
          </div>
          <div className="channel-desc">通过微信客服接收微信客户咨询，员工在 Seeme 内统一回复。</div>
        </div>
        <button className="icon-button" type="button" onClick={refreshWeCom} title="刷新状态">
          <RefreshCw size={16} />
        </button>
      </div>

      <div className="channel-summary-grid">
        <ChannelSummary icon={Check} title="启用状态" ok={status?.enabled} desc={status?.enabled ? '生产环境已启用' : '未启用'} />
        <ChannelSummary icon={KeyRound} title="接口凭据" ok={status?.configured} desc={status?.configured ? 'CorpID / Secret 已配置' : '凭据不完整'} />
        <ChannelSummary icon={MessageCircle} title="回调加解密" ok={status?.callbackTokenConfigured && status?.encodingAesKeyConfigured} desc={status?.callbackTokenConfigured && status?.encodingAesKeyConfigured ? 'Token / AESKey 已配置' : '回调配置不完整'} />
      </div>

      {notice && <div className="settings-success telegram-inline-alert">{notice}</div>}
      {error && <div className="settings-error telegram-inline-alert">{error}</div>}

      <div className="telegram-session-section">
        <div className="telegram-section-title">企业微信后台填写</div>
        <div className="telegram-session-form">
          <label>
            <span>回调 URL</span>
            <div className="channel-copy-row">
              <input value={WECOM_WEBHOOK_URL} readOnly />
              <button className="settings-save secondary" type="button" onClick={() => copyText(WECOM_WEBHOOK_URL, '回调 URL 已复制')}>复制</button>
            </div>
          </label>
          <label>
            <span>客服链接</span>
            <div className="channel-copy-row">
              <input value={WECOM_CONTACT_URL} readOnly />
              <button className="settings-save secondary" type="button" onClick={() => copyText(WECOM_CONTACT_URL, '客服链接已复制')}>复制</button>
            </div>
          </label>
        </div>
      </div>

      <div className="telegram-session-section">
        <div className="telegram-section-title">运行状态</div>
        <div className="channel-summary-grid">
          <ChannelSummary icon={Building2} title="CorpID" ok={status?.corpIdConfigured} desc={status?.corpIdConfigured ? '已配置' : '未配置'} />
          <ChannelSummary icon={KeyRound} title="Secret" ok={status?.secretConfigured} desc={status?.secretConfigured ? '已配置' : '未配置'} />
          <ChannelSummary icon={RefreshCw} title="同步任务" ok={!metrics?.failedJobs} desc={`待处理 ${metrics?.readyPendingJobs ?? 0}，失败 ${metrics?.failedJobs ?? 0}`} />
        </div>
      </div>

      <div className="telegram-session-section">
        <div className="email-panel-header">
          <div>
            <div className="telegram-section-title">企业员工</div>
            <div className="telegram-section-desc">从企业微信通讯录同步成员，并绑定到 ERP 用户；后续客户联系会按企业微信 userid 识别员工归属。</div>
          </div>
          <button className="settings-save secondary" type="button" disabled={busy} onClick={syncMembers}>
            <RefreshCw size={15} />
            <span>{busy ? '处理中' : '同步成员'}</span>
          </button>
        </div>
        {members.length ? (
          <div className="email-template-list compact">
            {members.map(member => (
              <div className="email-template-item" key={member.userid}>
                <div className="email-template-main">
                  <div className="email-template-name">{member.name || member.userid}</div>
                  <div className="email-template-meta">
                    {member.userid}
                    {member.position ? ` · ${member.position}` : ''}
                    {member.erpUserName ? ` · 已绑定 ${member.erpUserName}` : ' · 未绑定 ERP 用户'}
                  </div>
                </div>
                <div className="email-template-actions">
                  <select
                    className="telegram-session-select"
                    value={member.erpUserId || ''}
                    disabled={busy}
                    onChange={event => bindMember(member.userid, event.target.value)}
                  >
                    <option value="">未绑定</option>
                    {users.map(user => (
                      <option value={user.id} key={user.id}>{user.displayName} · {user.roleName}</option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="telegram-empty-state">尚未同步企业微信成员。同步失败时，请先在企业微信后台给 XIMI-ERP 自建应用配置通讯录可见范围。</div>
        )}
      </div>

      <div className="telegram-session-section">
        <div className="telegram-section-title">员工联系入口</div>
        <div className="telegram-section-desc">为企业微信员工生成客户联系「联系我」二维码；客户扫码后添加的是员工，不是客服帐号。</div>
        <div className="telegram-session-form">
          <TwoCol>
            <label>
              <span>企业微信成员</span>
              <select value={externalContactUserid} disabled={busy || !members.length} onChange={event => setExternalContactUserid(event.target.value)}>
                {members.map(member => (
                  <option value={member.userid} key={member.userid}>
                    {member.name || member.userid}{member.erpUserName ? ` · ${member.erpUserName}` : ''}
                  </option>
                ))}
              </select>
            </label>
            <Field label="来源 scene" value={externalContactScene} disabled={busy} onChange={setExternalContactScene} />
          </TwoCol>
          <Field label="备注" value={externalContactRemark} disabled={busy} placeholder="例如 官网首页 / 商品页 / 售后入口" onChange={setExternalContactRemark} />
          <button className="settings-save" type="button" disabled={busy || !externalContactUserid} onClick={createExternalContactWay}>
            <UserPlus size={15} />
            <span>{busy ? '生成中' : '生成员工联系二维码'}</span>
          </button>
          {externalContactQrCode && (
            <label>
              <span>生成结果</span>
              <div className="channel-copy-row">
                <input value={externalContactQrCode} readOnly />
                <button className="settings-save secondary" type="button" onClick={() => copyText(externalContactQrCode, '员工联系二维码链接已复制')}>复制</button>
              </div>
            </label>
          )}
        </div>
        {externalContactWays.length ? (
          <div className="email-template-list compact">
            {externalContactWays.map(way => (
              <div className="email-template-item" key={way.configId}>
                <div className="email-template-main">
                  <div className="email-template-name">{way.memberName || way.userid}</div>
                  <div className="email-template-meta">
                    {way.scene || 'erp'} · {way.erpUserName || '未绑定 ERP 用户'} · {way.configId}
                  </div>
                </div>
                <div className="email-template-actions">
                  <button className="settings-save secondary" type="button" onClick={() => copyText(way.qrCode, '员工联系二维码链接已复制')}>
                    复制链接
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="telegram-empty-state">还没有员工联系入口。同步并绑定成员后，可以为员工生成专属联系二维码。</div>
        )}
      </div>

      <div className="telegram-session-section">
        <div className="email-panel-header">
          <div>
            <div className="telegram-section-title">客服帐号</div>
            <div className="telegram-section-desc">从企业微信同步客服帐号，并配置新客户默认分配给哪个 ERP 员工。</div>
          </div>
          <button className="settings-save secondary" type="button" disabled={busy} onClick={syncAccounts}>
            <RefreshCw size={15} />
            <span>{busy ? '处理中' : '同步帐号'}</span>
          </button>
        </div>
        <div className="telegram-session-form compact">
          <label>
            <span>全局默认负责人</span>
            <select
              value={settings?.globalDefaultAssigneeId || ''}
              disabled={busy}
              onChange={event => saveGlobalAssignee(event.target.value)}
            >
              <option value="">未分配</option>
              {users.map(user => (
                <option value={user.id} key={user.id}>{user.displayName} · {user.roleName}</option>
              ))}
            </select>
          </label>
        </div>
        {accounts.length ? (
          <div className="email-template-list compact">
            {accounts.map(account => (
              <div className="email-template-item" key={account.openKfid}>
                <div className="email-template-main">
                  <div className="email-template-name">{account.name || account.openKfid}</div>
                  <div className="email-template-meta">{account.openKfid} · 默认负责人 {account.defaultAssigneeName || userName(settings?.globalDefaultAssigneeId) || '未分配'}</div>
                </div>
                <div className="email-template-actions">
                  <select
                    className="telegram-session-select"
                    value={account.defaultAssigneeId || ''}
                    disabled={busy}
                    onChange={event => saveAccountAssignee(account.openKfid, event.target.value)}
                  >
                    <option value="">使用全局默认</option>
                    {users.map(user => (
                      <option value={user.id} key={user.id}>{user.displayName}</option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="telegram-empty-state">尚未同步客服帐号。点击“同步帐号”从企业微信读取 open_kfid、名称和头像。</div>
        )}
      </div>

      <div className="telegram-session-section">
        <div className="telegram-section-title">生成客服链接</div>
        <div className="telegram-section-desc">链接可放到官网、订单页或售后页；scene 用于区分来源。</div>
        <div className="telegram-session-form">
          <TwoCol>
            <label>
              <span>客服帐号</span>
              <select value={contactWayOpenKfid} disabled={busy || !accounts.length} onChange={event => setContactWayOpenKfid(event.target.value)}>
                {accounts.map(account => (
                  <option value={account.openKfid} key={account.openKfid}>{account.name || account.openKfid}</option>
                ))}
              </select>
            </label>
            <Field label="scene" value={contactWayScene} disabled={busy} onChange={setContactWayScene} />
          </TwoCol>
          <button className="settings-save" type="button" disabled={busy || !contactWayOpenKfid} onClick={createContactWay}>
            <MessageCircle size={15} />
            <span>{busy ? '生成中' : '生成客服链接'}</span>
          </button>
          {contactWayUrl && (
            <label>
              <span>生成结果</span>
              <div className="channel-copy-row">
                <input value={contactWayUrl} readOnly />
                <button className="settings-save secondary" type="button" onClick={() => copyText(contactWayUrl, '客服链接已复制')}>复制</button>
              </div>
            </label>
          )}
        </div>
      </div>

      <div className="telegram-session-section">
        <div className="telegram-section-title">手动同步</div>
        <div className="telegram-section-desc">正常收消息由企业微信回调即时触发；这里只有排障时使用。</div>
        <div className="telegram-session-form">
          <label>
            <span>事件 Token</span>
            <input value={manualToken} disabled={busy} onChange={event => setManualToken(event.target.value)} />
          </label>
          <label>
            <span>open_kfid</span>
            <input value={manualOpenKfid} disabled={busy} placeholder="可选" onChange={event => setManualOpenKfid(event.target.value)} />
          </label>
          <button className="settings-save" type="button" disabled={busy} onClick={triggerSync}>
            <RefreshCw size={15} />
            <span>{busy ? '提交中' : '提交同步任务'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function WhatsAppChannel() {
  const [settings, setSettings] = useState();
  const [personalSessions, setPersonalSessions] = useState([]);
  const [personalQr, setPersonalQr] = useState({});
  const [personalLabel, setPersonalLabel] = useState('main');
  const [personalBusy, setPersonalBusy] = useState(false);
  const [form, setForm] = useState({
    enabled: false,
    phoneNumberId: '',
    accessToken: '',
    verifyToken: '',
    apiBaseUrl: 'https://graph.facebook.com/v21.0',
  });
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    refreshWhatsApp()
      .catch(err => setError(err.message));
  }, []);

  const refreshWhatsApp = async () => {
    const [cloud, sessions] = await Promise.all([
      api.getWhatsAppSettings(),
      api.listWhatsAppPersonalSessions().catch(() => []),
    ]);
    setSettings(cloud);
    setForm(current => ({
      ...current,
      enabled: Boolean(cloud?.enabled),
      phoneNumberId: cloud?.phoneNumberId || '',
      apiBaseUrl: cloud?.apiBaseUrl || current.apiBaseUrl,
    }));
    setPersonalSessions(sessions || []);
    return sessions || [];
  };

  const loadPersonalQr = async (sessionId) => {
    setError('');
    try {
      const qr = await api.getWhatsAppPersonalQr(sessionId);
      setPersonalQr(current => ({ ...current, [sessionId]: qr }));
      await refreshWhatsApp();
    } catch (err) {
      setError(err.message);
    }
  };

  const runPersonalAction = async (action, success) => {
    setPersonalBusy(true);
    setError('');
    setNotice('');
    try {
      const result = await action();
      const sessions = await refreshWhatsApp();
      if (result?.id) {
        await loadPersonalQr(result.id);
      } else {
        const pending = sessions.find(session => session.authState !== 'READY' && session.enabled);
        if (pending) {
          await loadPersonalQr(pending.id);
        }
      }
      setNotice(success);
    } catch (err) {
      setError(err.message);
    } finally {
      setPersonalBusy(false);
    }
  };

  useEffect(() => {
    const timer = window.setInterval(() => {
      api.listWhatsAppPersonalSessions()
        .then(sessions => {
          setPersonalSessions(sessions || []);
          const pending = (sessions || []).filter(session => session.enabled && session.authState !== 'READY');
          pending.forEach(session => {
            api.getWhatsAppPersonalQr(session.id)
              .then(qr => setPersonalQr(current => ({ ...current, [session.id]: qr })))
              .catch(() => undefined);
          });
        })
        .catch(() => undefined);
    }, 5000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    api.listWhatsAppPersonalSessions()
      .then(data => {
        const pending = (data || []).find(session => session.enabled && session.authState !== 'READY');
        if (pending) {
          loadPersonalQr(pending.id);
        }
      })
      .catch(() => undefined);
  }, []);

  const save = async () => {
    setError('');
    setNotice('');
    if (form.enabled && (!hasText(form.phoneNumberId) || (!hasText(form.accessToken) && !settings?.accessTokenConfigured))) {
      setError('启用 WhatsApp 前必须填写 Phone Number ID 和 Access Token');
      return;
    }
    try {
      const saved = await api.saveWhatsAppSettings(form);
      setSettings(saved);
      setForm(current => ({ ...current, accessToken: '', verifyToken: '' }));
      setNotice('WhatsApp 配置已保存');
    } catch (err) {
      setError(err.message);
    }
  };

  const webhookUrl = `${window.location.origin}/api/webhook/whatsapp`;

  return (
    <div className="email-workspace-panel">
      <div className="telegram-session-section">
        <div className="email-panel-header">
          <div>
            <div className="email-panel-title">WhatsApp 个人号</div>
            <div className="email-panel-desc">通过 WhatsApp Web 扫码登录真实个人账号，优先用于当前 WhatsApp 会话的文本收发。</div>
          </div>
          <div className="email-panel-actions">
            <button className="settings-save secondary" type="button" disabled={personalBusy} onClick={() => refreshWhatsApp()}>
              <RefreshCw size={15} />
              <span>刷新</span>
            </button>
          </div>
        </div>

        {error && <div className="tasks-error">{error}</div>}
        {notice && <div className="tasks-success">{notice}</div>}

        <div className="telegram-session-form compact">
          <TwoCol>
            <Field label="账号标签" value={personalLabel} disabled={personalBusy} onChange={setPersonalLabel} />
            <div className="telegram-action-row inline">
              <button
                className="settings-save"
                type="button"
                disabled={personalBusy || !personalLabel.trim()}
                onClick={() => runPersonalAction(() => api.createWhatsAppPersonalSession(personalLabel.trim()), 'WhatsApp 个人号 session 已创建，请扫码登录')}
              >
                <UserPlus size={15} />
                <span>新建扫码登录</span>
              </button>
            </div>
          </TwoCol>
        </div>

        {personalSessions.length ? (
          <div className="email-template-list compact">
            {personalSessions.map(session => {
              const qr = personalQr[session.id];
              return (
                <div className="email-template-item" key={session.id}>
                  <div className="email-template-main">
                    <div className="email-template-name">{session.label}</div>
                    <div className="email-template-meta">
                      {whatsappPersonalStateText(session.authState)} · {session.phoneNumber || session.accountName || '等待扫码'}
                      {session.lastError ? ` · ${session.lastError}` : ''}
                    </div>
                    {session.authState !== 'READY' && qr?.qrDataUrl && (
                      <div className="whatsapp-qr-panel">
                        <img src={qr.qrDataUrl} alt="WhatsApp 登录二维码" />
                      </div>
                    )}
                  </div>
                  <div className="email-template-actions">
                    <button className="telegram-session-action secondary" type="button" disabled={personalBusy} onClick={() => loadPersonalQr(session.id)}>
                      <RefreshCw size={14} />
                      <span>二维码</span>
                    </button>
                    <button
                      className="telegram-session-action secondary"
                      type="button"
                      disabled={personalBusy}
                      onClick={() => {
                        const next = window.prompt('新的账号标签', session.label);
                        if (next && next.trim() && next.trim() !== session.label) {
                          runPersonalAction(() => api.renameWhatsAppPersonalSession(session.id, next.trim()), '账号标签已更新');
                        }
                      }}
                    >
                      <Pencil size={14} />
                      <span>重命名</span>
                    </button>
                    <button className="telegram-session-action" type="button" disabled={personalBusy} onClick={() => runPersonalAction(() => api.closeWhatsAppPersonalSession(session.id), 'WhatsApp 个人号 session 已关闭')}>
                      <LogOut size={14} />
                      <span>关闭</span>
                    </button>
                    <button className="telegram-session-action danger" type="button" disabled={personalBusy} onClick={() => runPersonalAction(() => api.deleteWhatsAppPersonalSession(session.id), 'WhatsApp 个人号 session 已删除')}>
                      <Trash2 size={14} />
                      <span>删除</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="telegram-empty-state">还没有 WhatsApp 个人号 session。新建后用手机 WhatsApp 扫码登录。</div>
        )}

        <div className="channel-guide-note danger">
          个人号通过非官方 WhatsApp Web 协议接入，适合低频人工客服和客户跟进。不要用于群发、骚扰、爬取通讯录或高频自动化，否则有封号风险。
        </div>
      </div>

      <div className="telegram-session-section">
        <div className="email-panel-header">
          <div>
            <div className="email-panel-title">WhatsApp Cloud API</div>
            <div className="email-panel-desc">保留官方 Meta WhatsApp Business Cloud API 配置，个人号不可用时可作为正式通道。</div>
          </div>
          <button className="settings-save" type="button" onClick={save}>保存</button>
        </div>
        <div className="email-settings-grid">
          <label className="email-setting-field inline"><input type="checkbox" checked={form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked })} />启用 Cloud API</label>
          <label className="email-setting-field"><span>Phone Number ID</span><input value={form.phoneNumberId} onChange={e => setForm({ ...form, phoneNumberId: e.target.value })} /></label>
          <label className="email-setting-field"><span>Access Token</span><input type="password" value={form.accessToken} placeholder={settings?.accessTokenConfigured ? settings.accessTokenMasked : ''} onChange={e => setForm({ ...form, accessToken: e.target.value })} /></label>
          <label className="email-setting-field"><span>Verify Token</span><input type="password" value={form.verifyToken} placeholder={settings?.verifyTokenConfigured ? settings.verifyTokenMasked : ''} onChange={e => setForm({ ...form, verifyToken: e.target.value })} /></label>
          <label className="email-setting-field"><span>API Base URL</span><input value={form.apiBaseUrl} onChange={e => setForm({ ...form, apiBaseUrl: e.target.value })} /></label>
          <label className="email-setting-field"><span>Webhook URL</span><input readOnly value={webhookUrl} /></label>
        </div>
        <div className="channel-guide">
          <div className="channel-guide-title">怎么拿 WhatsApp 官方配置</div>
        <ol className="channel-guide-steps">
          <li>打开 <a href="https://developers.facebook.com/" target="_blank" rel="noreferrer">Meta for Developers</a>，进入你的 App；没有 App 就新建一个 Business 类型 App。</li>
          <li>在 App 里添加 WhatsApp 产品，进入 WhatsApp 的 API Setup 或 Getting started 页面。</li>
          <li>页面会显示 Phone number ID，把它复制到上面的 Phone Number ID。</li>
          <li>先用页面里的临时 Access Token 测试；正式使用时，建议在 Meta Business 设置里创建 System User，授权 WhatsApp Business Account 后生成长期 Token，再填到 Access Token。</li>
          <li>Verify Token 是你自己设置的一段校验密码。先在这里填写并保存，再去 Meta Webhooks 配置里填同一段值。</li>
          <li>在 Meta Webhooks 里，Callback URL 填上面的 Webhook URL，Verify Token 填同一个 Verify Token，然后订阅 messages 事件。</li>
          <li>客户资料里的 WhatsApp 要填客户手机号，建议使用国际格式，例如 8613812345678，不要带加号、空格或横线。</li>
        </ol>
        <div className="channel-guide-note">
          注意：WhatsApp Cloud API 只支持已接入 Meta WhatsApp Business Platform 的号码。普通 WhatsApp 或 WhatsApp Business App 手机端号码不能直接拿来发 API 消息。
        </div>
        </div>
      </div>
    </div>
  );
}

function TeamsChannel() {
  const [settings, setSettings] = useState();
  const [form, setForm] = useState({
    enabled: false,
    accessToken: '',
    webhookSecret: '',
    graphBaseUrl: 'https://graph.microsoft.com/v1.0',
    tenantId: 'organizations',
    clientId: '',
    clientSecret: '',
    redirectUri: `${window.location.origin}/api/channel/teams/oauth/callback`,
  });
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    api.getTeamsSettings()
      .then(data => {
        setSettings(data);
        setForm(current => ({
          ...current,
          enabled: Boolean(data?.enabled),
          graphBaseUrl: data?.graphBaseUrl || current.graphBaseUrl,
          tenantId: data?.tenantId || current.tenantId,
          clientId: data?.clientId || '',
          redirectUri: data?.redirectUri || current.redirectUri,
        }));
      })
      .catch(err => setError(err.message));
  }, []);

  const save = async () => {
    setError('');
    setNotice('');
    if (form.enabled && !hasText(form.accessToken) && !settings?.accessTokenConfigured) {
      setError('启用 Teams 前必须填写 Microsoft Graph Access Token');
      return;
    }
    try {
      const saved = await api.saveTeamsSettings(form);
      setSettings(saved);
      setForm(current => ({ ...current, accessToken: '', webhookSecret: '' }));
      setNotice('Teams 配置已保存');
    } catch (err) {
      setError(err.message);
    }
  };

  const loginWithMicrosoft = async () => {
    setError('');
    setNotice('');
    try {
      await save();
      const result = await api.getTeamsOAuthUrl();
      if (result?.authorizationUrl) {
        window.location.href = result.authorizationUrl;
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const webhookUrl = `${window.location.origin}/api/webhook/teams`;
  const oauthSuccess = new URLSearchParams(window.location.search).get('teams_oauth') === 'success';

  return (
    <div className="email-workspace-panel">
      <div className="email-panel-header">
        <div>
          <div className="email-panel-title">Microsoft Teams</div>
          <div className="email-panel-desc">使用 Microsoft Graph 向已配置 chatId 的 Teams 会话发送文本消息。入站消息可由 Graph 订阅或自动化流程转发到受密钥保护的 Webhook。</div>
        </div>
        <div className="email-panel-actions">
          <button className="settings-save secondary" type="button" onClick={loginWithMicrosoft}>Microsoft 登录</button>
          <button className="settings-save" type="button" onClick={save}>保存</button>
        </div>
      </div>
      {error && <div className="tasks-error">{error}</div>}
      {notice && <div className="tasks-success">{notice}</div>}
      {oauthSuccess && <div className="tasks-success">Microsoft Teams 账号已授权，可使用当前登录账号发送消息。</div>}
      {settings?.accountName && <div className="settings-success">已登录 Teams 账号：{settings.accountName}</div>}
      <div className="email-settings-grid">
        <label className="email-setting-field inline"><input type="checkbox" checked={form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked })} />启用 Teams</label>
        <label className="email-setting-field"><span>Tenant</span><input value={form.tenantId} placeholder="organizations" onChange={e => setForm({ ...form, tenantId: e.target.value })} /></label>
        <label className="email-setting-field"><span>Client ID</span><input value={form.clientId} onChange={e => setForm({ ...form, clientId: e.target.value })} /></label>
        <label className="email-setting-field"><span>Client Secret</span><input type="password" value={form.clientSecret} placeholder={settings?.clientSecretConfigured ? settings.clientSecretMasked : ''} onChange={e => setForm({ ...form, clientSecret: e.target.value })} /></label>
        <label className="email-setting-field"><span>Redirect URI</span><input value={form.redirectUri} onChange={e => setForm({ ...form, redirectUri: e.target.value })} /></label>
        <label className="email-setting-field"><span>Graph Access Token</span><input type="password" value={form.accessToken} placeholder={settings?.accessTokenConfigured ? settings.accessTokenMasked : ''} onChange={e => setForm({ ...form, accessToken: e.target.value })} /></label>
        <label className="email-setting-field"><span>Webhook Secret</span><input type="password" value={form.webhookSecret} placeholder={settings?.webhookSecretConfigured ? settings.webhookSecretMasked : ''} onChange={e => setForm({ ...form, webhookSecret: e.target.value })} /></label>
        <label className="email-setting-field"><span>Graph Base URL</span><input value={form.graphBaseUrl} onChange={e => setForm({ ...form, graphBaseUrl: e.target.value })} /></label>
        <label className="email-setting-field"><span>Webhook URL</span><input readOnly value={webhookUrl} /></label>
      </div>
      <div className="channel-guide">
        <div className="channel-guide-title">怎么拿 Teams 配置</div>
        <ol className="channel-guide-steps">
          <li>打开 <a href="https://entra.microsoft.com/" target="_blank" rel="noreferrer">Microsoft Entra admin center</a>，进入 App registrations，新建或选择一个应用。</li>
          <li>在应用的 Authentication 里添加 Web 平台 Redirect URI，值填上面 Redirect URI 输入框里的地址。</li>
          <li>在 Certificates & secrets 里创建 Client secret，把 Secret 的 Value 填到 Client Secret。注意不是 Secret ID。</li>
          <li>在 API permissions 里添加 Microsoft Graph 权限。当前发送聊天消息需要能调用 Graph chat message API，常用 delegated 权限是 ChatMessage.Send，并由管理员同意。</li>
          <li>保存后点击 Microsoft 登录，使用 Microsoft 365 工作/学校账号授权。系统会保存 access token 和 refresh token，发送消息时自动刷新。</li>
          <li>客户资料里的 Teams chatId 要填 Microsoft Graph 的 chat id。可以用 Graph Explorer 或自己的 OAuth 工具调用 GET /me/chats 找到对应会话 id。</li>
          <li>Webhook Secret 是你自己设置的一段密钥。保存后，外部转发入站消息到本系统时，请在请求头加入 x-seeme-webhook-secret，并填同一段值。</li>
          <li>入站消息转发地址填上面的 Webhook URL。请求内容里至少带 chatId 或 conversationId、发送人 id、文本内容，系统会自动创建或匹配联系人和会话。</li>
        </ol>
        <div className="channel-guide-note">
          个人账号支持结论：Microsoft Graph 的 Teams 发消息接口不支持普通个人 Microsoft 账号，只支持工作/学校账号。这里的 Microsoft 登录是“某个员工自己的 Microsoft 365 账号登录”，不是 Outlook/Hotmail 这类个人账号。
        </div>
      </div>
    </div>
  );
}

function TelegramChannel() {
  const [telegramStatus, setTelegramStatus] = useState();
  const [telegramSessions, setTelegramSessions] = useState([]);
  const [telegramBotSettings, setTelegramBotSettings] = useState();
  const [telegramBotForm, setTelegramBotForm] = useState({
    botToken: '',
    webhookSecret: '',
    apiBaseUrl: 'https://api.telegram.org',
    proxyHost: '',
    proxyPort: '',
  });
  const [telegramForm, setTelegramForm] = useState({ label: 'main', phoneNumber: '', code: '', password: '' });
  const [selectedTelegramSessionId, setSelectedTelegramSessionId] = useState('');
  const [telegramBusy, setTelegramBusy] = useState(false);
  const [telegramError, setTelegramError] = useState('');
  const [telegramNotice, setTelegramNotice] = useState('');
  const [telegramContactQuery, setTelegramContactQuery] = useState('');
  const [telegramContactResults, setTelegramContactResults] = useState([]);
  const [telegramContactBusy, setTelegramContactBusy] = useState(false);
  const [telegramContactError, setTelegramContactError] = useState('');
  const [telegramContactNotice, setTelegramContactNotice] = useState('');

  useEffect(() => {
    let active = true;
    refreshTelegram(active);
    return () => {
      active = false;
    };
  }, []);

  const refreshTelegram = (active = true) => {
    return Promise.all([
      api.getTelegramStatus().catch(() => null),
      api.getTelegramBotSettings().catch(() => null),
      api.listTelegramClientSessions().catch(() => []),
    ]).then(([status, botSettings, sessions]) => {
      if (!active) return;
      setTelegramStatus(status);
      setTelegramBotSettings(botSettings);
      setTelegramSessions(sessions || []);
      if (botSettings) {
        setTelegramBotForm(current => ({
          ...current,
          apiBaseUrl: botSettings.apiBaseUrl || current.apiBaseUrl || 'https://api.telegram.org',
          proxyHost: botSettings.proxyHost || '',
          proxyPort: botSettings.proxyPort ? String(botSettings.proxyPort) : '',
        }));
      }
      const preferredLabel = status?.clientApi?.accountLabel || sessions?.[0]?.label || 'main';
      const currentSession = sessions?.find(session => session.id === selectedTelegramSessionId)
        || sessions?.find(session => session.enabled && session.authState !== 'CLOSED');
      if (!selectedTelegramSessionId && currentSession?.id) {
        setSelectedTelegramSessionId(currentSession.id);
      }
      setTelegramForm(current => ({
        ...current,
        label: current.label || preferredLabel,
        phoneNumber: current.phoneNumber || currentSession?.phoneNumber || '',
      }));
    });
  };

  const runTelegramAction = async (action, notice) => {
    setTelegramBusy(true);
    setTelegramError('');
    setTelegramNotice('');
    try {
      await action();
      setTelegramNotice(notice || '操作已提交');
      return await refreshTelegram(true);
    } catch (error) {
      setTelegramError(error.message || 'Telegram 操作失败');
    } finally {
      setTelegramBusy(false);
    }
  };

  const saveTelegramBot = () => runTelegramAction(
    () => api.saveTelegramBotSettings({
      botToken: telegramBotForm.botToken.trim(),
      webhookSecret: telegramBotForm.webhookSecret.trim(),
      apiBaseUrl: telegramBotForm.apiBaseUrl.trim() || 'https://api.telegram.org',
      proxyHost: telegramBotForm.proxyHost.trim(),
      proxyPort: telegramBotForm.proxyPort.trim() ? Number(telegramBotForm.proxyPort.trim()) : 0,
    }),
    'Bot API 配置已保存'
  );

  const configureTelegramWebhook = () => {
    const origin = window.location.origin.replace(/\/+$/, '');
    return runTelegramAction(
      () => api.configureTelegramBotWebhook(`${origin}/api/webhook/telegram`),
      'Webhook 已提交到 Telegram'
    );
  };

  const renameTelegramSession = (session) => {
    const nextLabel = window.prompt('请输入新的账号标签', session.label);
    if (nextLabel == null) return;
    const normalized = nextLabel.trim();
    if (!normalized || normalized === session.label) return;
    runTelegramAction(() => api.renameTelegramClientSession(session.id, normalized), '账号标签已更新');
  };

  const deleteTelegramSession = (session) => {
    if (!window.confirm(`确认删除 Telegram 账号「${session.label}」？删除后需要重新创建并验证。`)) return;
    runTelegramAction(
      async () => {
        await api.deleteTelegramClientSession(session.id);
        if (selectedTelegramSessionId === session.id) {
          setSelectedTelegramSessionId('');
          setTelegramForm(current => ({ ...current, phoneNumber: '', code: '', password: '' }));
        }
      },
      '账号已删除'
    );
  };

  const searchTelegramContacts = async () => {
    const query = telegramContactQuery.trim();
    if (!query) {
      setTelegramContactError('请输入 @username、姓名或手机号');
      return;
    }
    if (!readyTelegramSession) {
      setTelegramContactError('请先选择一个已登录的 Telegram 用户号账号');
      return;
    }
    setTelegramContactBusy(true);
    setTelegramContactError('');
    setTelegramContactNotice('');
    try {
      const results = await api.searchTelegramContacts(readyTelegramSession.id, query, 10);
      setTelegramContactResults(results || []);
      if (!results?.length) {
        setTelegramContactNotice('没有查到匹配用户；如果这是手机号，可以直接按手机号添加');
      }
    } catch (error) {
      setTelegramContactError(error.message || '查询 Telegram 好友失败');
    } finally {
      setTelegramContactBusy(false);
    }
  };

  const addTelegramContact = async (contact) => {
    if (!readyTelegramSession) {
      setTelegramContactError('请先选择一个已登录的 Telegram 用户号账号');
      return;
    }
    const payload = contact
      ? { userId: contact.id, username: contact.username || '', firstName: contact.firstName || '', lastName: contact.lastName || '' }
      : { phoneNumber: telegramContactQuery.trim(), firstName: telegramContactQuery.trim() };
    if (!contact && !/^\+?\d[\d\s-]{5,}$/.test(payload.phoneNumber)) {
      setTelegramContactError('按手机号添加时请输入完整手机号，例如 +8613800000000');
      return;
    }
    setTelegramContactBusy(true);
    setTelegramContactError('');
    setTelegramContactNotice('');
    try {
      const added = await api.addTelegramContact(readyTelegramSession.id, payload);
      setTelegramContactNotice(`已添加 ${telegramContactName(added)}`);
    } catch (error) {
      setTelegramContactError(error.message || '添加 Telegram 好友失败');
    } finally {
      setTelegramContactBusy(false);
    }
  };

  const selectedTelegramSession = telegramSessions.find(session => session.id === selectedTelegramSessionId);
  const readyTelegramSessions = telegramSessions.filter(session => session.enabled && session.authState === 'READY');
  const readyTelegramSession = readyTelegramSessions.find(session => session.id === selectedTelegramSessionId) || readyTelegramSessions[0];
  const activeTelegramSession = selectedTelegramSession && !['READY', 'CLOSED'].includes(selectedTelegramSession.authState)
    ? selectedTelegramSession
    : telegramSessions.find(session => session.enabled && !['READY', 'CLOSED'].includes(session.authState));
  const telegramConfigured = telegramStatus?.clientApi?.configured;
  const telegramEnabled = telegramStatus?.clientApi?.enabled;
  const telegramAuthState = activeTelegramSession?.authState || '';
  const needsPhone = activeTelegramSession && ['WAIT_PHONE_NUMBER', 'FAILED', 'STARTING'].includes(telegramAuthState);
  const needsCode = activeTelegramSession && telegramAuthState === 'WAIT_CODE';
  const needsPassword = activeTelegramSession && telegramAuthState === 'WAIT_PASSWORD';
  const pendingTelegramSessions = telegramSessions.filter(session => session.enabled && !['READY', 'FAILED', 'CLOSED'].includes(session.authState));
  const pendingTelegramSessionKey = pendingTelegramSessions.map(session => `${session.id}:${session.authState}`).join('|');

  useEffect(() => {
    if (!pendingTelegramSessions.length) return undefined;
    const timer = window.setInterval(() => refreshTelegram(true), 5000);
    return () => window.clearInterval(timer);
  }, [pendingTelegramSessionKey]);

  return (
    <div className="channel-panel-stack">
      <section className="settings-panel">
        <div className="settings-panel-title">
          <Send size={15} />
          <span>Telegram</span>
        </div>
        <div className="channel-summary-grid">
          <ChannelSummary icon={Send} title="Bot API" ok={telegramStatus?.botTokenConfigured} desc={`Webhook：${telegramStatus?.webhookSecretConfigured ? '已启用且已接收推送' : '未启用密钥校验'}${telegramStatus?.proxyConfigured ? '，代理已配置' : ''}`} />
          <ChannelSummary icon={Users} title="Client API" ok={telegramStatus?.clientApi?.configured} desc={telegramStatus?.clientApi?.configured ? `Telegram App：${telegramStatus.clientApi.chatScope || 'private'}` : '未配置 api_id / api_hash'} />
          <ChannelSummary icon={UserPlus} title="用户与账号" ok={telegramSessions.some(session => session.authState === 'READY')} desc={`${telegramSessions.filter(session => session.authState === 'READY').length} 个已登录`} />
        </div>

        <div className="telegram-session-section bot-api">
          <div className="telegram-section-title">Bot API 配置</div>
          <div className="telegram-session-form">
            <label className="telegram-field-token">
              <span>Bot Token</span>
              <input type="password" placeholder={telegramBotSettings?.botTokenMasked || '123456:telegram-bot-token'} value={telegramBotForm.botToken} disabled={telegramBusy} onChange={event => setTelegramBotForm({ ...telegramBotForm, botToken: event.target.value })} />
            </label>
            <label className="telegram-field-secret">
              <span>Webhook Secret</span>
              <input type="password" placeholder={telegramBotSettings?.webhookSecretMasked || '建议使用随机长字符串'} value={telegramBotForm.webhookSecret} disabled={telegramBusy} onChange={event => setTelegramBotForm({ ...telegramBotForm, webhookSecret: event.target.value })} />
            </label>
            <label className="telegram-field-api">
              <span>API Base URL</span>
              <input value={telegramBotForm.apiBaseUrl} disabled={telegramBusy} onChange={event => setTelegramBotForm({ ...telegramBotForm, apiBaseUrl: event.target.value })} />
            </label>
            <div className="telegram-proxy-row">
              <label>
                <span>代理 Host</span>
                <input value={telegramBotForm.proxyHost} disabled={telegramBusy} onChange={event => setTelegramBotForm({ ...telegramBotForm, proxyHost: event.target.value })} />
              </label>
              <label>
                <span>代理 Port</span>
                <input inputMode="numeric" value={telegramBotForm.proxyPort} disabled={telegramBusy} onChange={event => setTelegramBotForm({ ...telegramBotForm, proxyPort: event.target.value })} />
              </label>
            </div>
            <div className="telegram-action-row">
              <button className="settings-save" type="button" disabled={telegramBusy} onClick={saveTelegramBot}>
                <Check size={15} />
                <span>保存 Bot API</span>
              </button>
              <button className="settings-save secondary" type="button" disabled={telegramBusy || !telegramStatus?.botTokenConfigured} onClick={configureTelegramWebhook}>
                <Send size={15} />
                <span>设置 Webhook</span>
              </button>
            </div>
          </div>
        </div>

        <div className="telegram-session-box">
          <div className="telegram-session-head">
            <div>
              <div className="settings-row-title">Telegram 用户号账号</div>
              <div className="settings-row-desc">{telegramEnabled ? '已登录账号会同时接收消息，未完成账号可在列表内继续验证' : '需要启用 TELEGRAM_CLIENT_ENABLED'}</div>
            </div>
            <button className="icon-button" type="button" onClick={() => refreshTelegram(true)} disabled={telegramBusy}>
              <RefreshCw size={15} />
            </button>
          </div>

          <div className="telegram-session-section account-list-section">
            <div className="telegram-section-title">账号列表</div>
            {telegramSessions.length > 0 ? (
              <div className="telegram-session-list">
                <div className="telegram-session-table-head">
                  <span>账号标签</span>
                  <span>用户名 / 手机号</span>
                  <span>状态</span>
                  <span>操作</span>
                </div>
                {telegramSessions.map(session => (
                  <div key={session.id} className={`telegram-session-item ${session.id === activeTelegramSession?.id ? 'active' : ''}`}>
                    <span className="telegram-session-name">
                      <span className="telegram-session-avatar">{session.label?.slice(0, 2).toUpperCase() || 'TG'}</span>
                      <span>{session.label}</span>
                    </span>
                    <span className="telegram-session-phone">{session.phoneNumber || '未提交手机号'}</span>
                    <span className={`telegram-session-state ${session.authState === 'READY' ? 'ok' : ''}`}>{telegramStateText(session.authState)}</span>
                    <div className="telegram-session-actions">
                      <button
                        className={`telegram-session-action ${session.authState === 'READY' ? 'danger' : ''}`}
                        type="button"
                        disabled={telegramBusy}
                        onClick={() => {
                          if (session.authState === 'READY') {
                            runTelegramAction(() => api.closeTelegramClientSession(session.id), 'Session 已关闭');
                            return;
                          }
                          runTelegramAction(async () => {
                            if (session.authState === 'CLOSED') {
                              const reopened = await api.createTelegramClientSession(session.label);
                              setSelectedTelegramSessionId(reopened.id);
                              return reopened;
                            }
                            setSelectedTelegramSessionId(session.id);
                            setTelegramForm(current => ({ ...current, phoneNumber: session.phoneNumber || '', code: '', password: '' }));
                            return session;
                          }, session.authState === 'CLOSED' ? 'Session 已启用，请提交手机号' : '请继续完成账号验证');
                        }}
                      >
                        {session.authState === 'READY' ? <><LogOut size={14} />退出登录</> : session.authState === 'CLOSED' ? '启用' : '继续验证'}
                      </button>
                      <button className="telegram-session-action secondary" type="button" disabled={telegramBusy} onClick={() => renameTelegramSession(session)}><Pencil size={14} />重命名</button>
                      <button className="telegram-session-action danger" type="button" disabled={telegramBusy} onClick={() => deleteTelegramSession(session)}><Trash2 size={14} />删除</button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="telegram-empty-state">还没有添加 Telegram 用户号。</div>
            )}
          </div>

          <div className="telegram-session-section contacts">
            <div className="telegram-section-title">查找/添加 TG 好友</div>
            <div className="telegram-section-desc">使用已登录的用户号查询 Telegram 用户，支持 @username、姓名或手机号；手机号可直接导入通讯录。</div>
            <div className="telegram-contact-form">
              <label>
                <span>发送账号</span>
                <select value={readyTelegramSession?.id || ''} disabled={telegramContactBusy || !readyTelegramSessions.length} onChange={event => setSelectedTelegramSessionId(event.target.value)}>
                  {readyTelegramSessions.length ? readyTelegramSessions.map(session => (
                    <option key={session.id} value={session.id}>{session.label}{session.phoneNumber ? ` · ${session.phoneNumber}` : ''}</option>
                  )) : <option value="">暂无已登录账号</option>}
                </select>
              </label>
              <label>
                <span>查询内容</span>
                <input value={telegramContactQuery} disabled={telegramContactBusy || !readyTelegramSessions.length} placeholder="@username / 姓名 / +8613800000000" onChange={event => setTelegramContactQuery(event.target.value)} onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); searchTelegramContacts(); } }} />
              </label>
              <button className="settings-save" type="button" disabled={telegramContactBusy || !readyTelegramSessions.length || !telegramContactQuery.trim()} onClick={searchTelegramContacts}>
                <Search size={15} />
                <span>{telegramContactBusy ? '处理中' : '查询'}</span>
              </button>
              <button className="settings-save secondary" type="button" disabled={telegramContactBusy || !readyTelegramSessions.length || !telegramContactQuery.trim()} onClick={() => addTelegramContact(null)}>
                <UserPlus size={15} />
                <span>按手机号添加</span>
              </button>
            </div>
            {telegramContactResults.length > 0 && (
              <div className="telegram-contact-results">
                {telegramContactResults.map(contact => (
                  <div className="telegram-contact-item" key={contact.id}>
                    <div>
                      <div className="telegram-contact-name">{telegramContactName(contact)}</div>
                      <div className="telegram-contact-meta">{contact.username ? `@${contact.username}` : `ID ${contact.id}`}{contact.mutual ? ' · 共同联系人' : ''}{contact.bot ? ' · Bot' : ''}</div>
                    </div>
                    <button className="telegram-session-action" type="button" disabled={telegramContactBusy || contact.bot} onClick={() => addTelegramContact(contact)}>添加</button>
                  </div>
                ))}
              </div>
            )}
            {telegramContactNotice && <div className="settings-success telegram-inline-alert">{telegramContactNotice}</div>}
            {telegramContactError && <div className="settings-error telegram-inline-alert">{telegramContactError}</div>}
          </div>

          <div className="telegram-session-section create">
            <div className="telegram-section-title">新增账号</div>
            <div className="telegram-section-desc">标签只用于页面区分账号，不需要和 Telegram 用户名一致。</div>
            <div className="telegram-session-form compact">
              <label>
                <span>账号标签</span>
                <input value={telegramForm.label} disabled={telegramBusy} onChange={event => setTelegramForm({ ...telegramForm, label: event.target.value })} />
              </label>
              <button className="settings-save" type="button" disabled={telegramBusy || !telegramConfigured || !telegramEnabled || !telegramForm.label.trim()} onClick={() => runTelegramAction(async () => {
                const session = await api.createTelegramClientSession(telegramForm.label.trim());
                setSelectedTelegramSessionId(session.id);
                return session;
              }, 'Session 已创建，请提交手机号')}>
                <KeyRound size={15} />
                <span>创建/启用 Session</span>
              </button>
            </div>
          </div>

          {activeTelegramSession && (
            <div className="telegram-session-section auth">
              <div className="telegram-section-title">账号验证</div>
              <div className="telegram-session-form">
                <div className="telegram-step">{telegramStepText(telegramAuthState)}</div>
                {needsPhone && (
                  <>
                    <label>
                      <span>手机号</span>
                      <input placeholder="+8613800000000" value={telegramForm.phoneNumber} disabled={telegramBusy} onChange={event => setTelegramForm({ ...telegramForm, phoneNumber: event.target.value })} />
                    </label>
                    <button className="settings-save" type="button" disabled={telegramBusy || !telegramForm.phoneNumber.trim()} onClick={() => runTelegramAction(() => api.submitTelegramClientPhone(activeTelegramSession.id, telegramForm.phoneNumber.trim()), '手机号已提交，请查看 Telegram 验证码')}>
                      <Smartphone size={15} />
                      <span>{telegramBusy ? '提交中' : '提交手机号'}</span>
                    </button>
                  </>
                )}
                {needsCode && (
                  <>
                    <label>
                      <span>验证码</span>
                      <input value={telegramForm.code} disabled={telegramBusy} onChange={event => setTelegramForm({ ...telegramForm, code: event.target.value })} />
                    </label>
                    <button className="settings-save" type="button" disabled={telegramBusy || !telegramForm.code.trim()} onClick={() => runTelegramAction(() => api.submitTelegramClientCode(activeTelegramSession.id, telegramForm.code.trim()), '验证码已提交')}>
                      <Check size={15} />
                      <span>{telegramBusy ? '提交中' : '提交验证码'}</span>
                    </button>
                  </>
                )}
                {needsPassword && (
                  <>
                    <label>
                      <span>Telegram 2FA 密码</span>
                      <input type="password" value={telegramForm.password} disabled={telegramBusy} onChange={event => setTelegramForm({ ...telegramForm, password: event.target.value })} />
                    </label>
                    <button className="settings-save" type="button" disabled={telegramBusy || !telegramForm.password} onClick={() => runTelegramAction(() => api.submitTelegramClientPassword(activeTelegramSession.id, telegramForm.password), '2FA 密码已提交，正在确认登录状态')}>
                      <KeyRound size={15} />
                      <span>{telegramBusy ? '提交中' : '提交 2FA'}</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
          {telegramNotice && <div className="settings-success telegram-inline-alert">{telegramNotice}</div>}
          {telegramError && <div className="settings-error telegram-inline-alert">{telegramError}</div>}
        </div>
      </section>
    </div>
  );
}

function EmailChannel() {
  const [form, setForm] = useState(EMPTY_EMAIL_FORM);
  const [templates, setTemplates] = useState([]);
  const [activeView, setActiveView] = useState('settings');
  const [templateForm, setTemplateForm] = useState(EMPTY_EMAIL_TEMPLATE_FORM);
  const [editingTemplateId, setEditingTemplateId] = useState('');
  const [drawerMode, setDrawerMode] = useState('');
  const [settings, setSettings] = useState();
  const [status, setStatus] = useState();
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const selectedTemplateId = detectEmailProviderTemplate(form, templates);
  const isTemplateDrawerOpen = Boolean(drawerMode);
  const isTemplateDrawerReadOnly = drawerMode === 'view';
  const editingTemplate = templates.find(item => item.id === editingTemplateId);

  useEffect(() => {
    refreshEmail();
  }, []);

  const refreshEmail = async () => {
    const [nextSettings, nextStatus, nextTemplates] = await Promise.all([
      api.getEmailSettings().catch(() => null),
      api.getEmailStatus().catch(() => null),
      api.listEmailTemplates().catch(() => []),
    ]);
    if (nextSettings) {
      setSettings(nextSettings);
      setForm({
        enabled: Boolean(nextSettings.enabled),
        host: nextSettings.host || '',
        port: String(nextSettings.port || 587),
        username: nextSettings.username || '',
        password: '',
        from: nextSettings.from || '',
        auth: Boolean(nextSettings.auth),
        startTls: Boolean(nextSettings.startTls),
        defaultSubject: nextSettings.defaultSubject || 'Seeme message',
        inboundEnabled: Boolean(nextSettings.inboundEnabled),
        imapHost: nextSettings.imapHost || '',
        imapPort: String(nextSettings.imapPort || 993),
        imapUsername: nextSettings.imapUsername || '',
        imapPassword: '',
        imapProtocol: nextSettings.imapProtocol || 'imaps',
        imapFolder: nextSettings.imapFolder || 'INBOX',
      });
    }
    setTemplates(nextTemplates || []);
    setStatus(nextStatus);
  };

  const resetTemplateEditor = () => {
    setEditingTemplateId('');
    setTemplateForm(EMPTY_EMAIL_TEMPLATE_FORM);
    setDrawerMode('');
  };

  const openTemplateDrawer = (mode, template = null) => {
    setDrawerMode(mode);
    setEditingTemplateId(template?.id || '');
    setTemplateForm(mode === 'create' ? EMPTY_EMAIL_TEMPLATE_FORM : templateFormFromTemplate(template));
    setError('');
    setNotice('');
  };

  const saveEmail = async () => {
    const smtpPort = Number(form.port);
    const imapPort = Number(form.imapPort);
    const validationError = validateEmailSettingsForm(form, settings);
    if (validationError) {
      setError(validationError);
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const payload = {
        ...form,
        port: smtpPort,
        imapPort,
      };
      const saved = await api.saveEmailSettings(payload);
      setSettings(saved);
      setForm(current => ({ ...current, password: '', imapPassword: '' }));
      setNotice('邮箱配置已保存');
      await refreshEmail();
    } catch (saveError) {
      setError(saveError.message || '邮箱配置保存失败');
    } finally {
      setBusy(false);
    }
  };

  const saveTemplate = async () => {
    const validationError = validateEmailTemplateForm(templateForm);
    if (validationError) {
      setError(validationError);
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const payload = {
        ...templateForm,
        port: Number(templateForm.port),
        imapPort: Number(templateForm.imapPort),
      };
      const saved = editingTemplateId
        ? await api.updateEmailTemplate(editingTemplateId, payload)
        : await api.createEmailTemplate(payload);
      setNotice(editingTemplateId ? '邮箱模板已更新' : '邮箱模板已新增');
      resetTemplateEditor();
      await refreshEmail();
      if (!selectedTemplateId || selectedTemplateId === 'custom') {
        setForm(current => applyEmailProviderTemplate(current, saved));
      }
    } catch (templateError) {
      setError(templateError.message || '邮箱模板保存失败');
    } finally {
      setBusy(false);
    }
  };

  const editTemplate = (template) => {
    openTemplateDrawer('edit', template);
  };

  const deleteTemplate = async (template) => {
    if (!window.confirm(`确认删除邮箱模板「${template.name}」？`)) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await api.deleteEmailTemplate(template.id);
      if (editingTemplateId === template.id) {
        resetTemplateEditor();
      }
      setNotice('邮箱模板已删除');
      await refreshEmail();
    } catch (deleteError) {
      setError(deleteError.message || '邮箱模板删除失败');
    } finally {
      setBusy(false);
    }
  };

  const pollEmail = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const nextStatus = await api.pollEmailInbox();
      setStatus(nextStatus);
      setNotice(`收信完成：扫描 ${nextStatus.lastScanned || 0}，导入 ${nextStatus.lastImported || 0}`);
    } catch (pollError) {
      setError(pollError.message || '收信失败');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="channel-panel-stack">
      <section className="settings-panel">
        <div className="settings-panel-title">
          <Mail size={15} />
          <span>个人邮箱</span>
        </div>
        <div className="channel-summary-grid">
          <ChannelSummary title="当前账号 SMTP" ok={settings?.enabled} desc={settings?.host ? `${settings.host}:${settings.port} · ${settings.from || '未设置 From'}` : '未配置'} />
          <ChannelSummary title="当前账号 IMAP" ok={settings?.inboundEnabled} desc={settings?.imapHost ? `${settings.imapHost}:${settings.imapPort} · ${settings.imapFolder || 'INBOX'}` : '未配置'} />
          <ChannelSummary title="当前账号最近收信" ok={!status?.lastError} desc={status?.lastError || `扫描 ${status?.lastScanned || 0}，导入 ${status?.lastImported || 0}`} />
        </div>
        <div className="email-workspace-tabs" role="tablist" aria-label="邮箱管理视图">
          {EMAIL_WORKSPACE_TABS.map(tab => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeView === tab.id}
              className={`email-workspace-tab ${activeView === tab.id ? 'active' : ''}`}
              onClick={() => setActiveView(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {notice && <div className="settings-success telegram-inline-alert">{notice}</div>}
        {error && <div className="settings-error telegram-inline-alert">{error}</div>}

        {activeView === 'settings' && (
          <div className="email-workspace-panel">
            <div className="telegram-session-section">
              <div className="email-panel-header">
                <div>
                  <div className="telegram-section-title">当前邮箱配置</div>
                  <div className="telegram-section-desc">这一页只处理当前登录账号的发信、收信与模板套用，减少无关信息干扰。</div>
                </div>
                <div className="email-panel-actions">
                  <button className="settings-save secondary" type="button" disabled={busy} onClick={refreshEmail}>
                    <RefreshCw size={15} />
                    <span>刷新状态</span>
                  </button>
                </div>
              </div>
              <div className="email-settings-toolbar">
                <label className="email-template-picker">
                  <span>快速套用模板</span>
                  <select
                    value={selectedTemplateId}
                    disabled={busy}
                    onChange={event => {
                      const template = templates.find(item => item.id === event.target.value);
                      if (!template) return;
                      setForm(current => applyEmailProviderTemplate(current, template));
                    }}
                  >
                    <option value="custom">自定义 / 当前配置</option>
                    {templates.map(template => (
                      <option key={template.id} value={template.id}>{template.name}</option>
                    ))}
                  </select>
                </label>
                <div className="email-template-picker-note">
                  选择模板只会覆盖服务器地址、端口、协议和常见开关，不会改掉用户名、密码、From 地址和默认主题。
                </div>
                <button className="settings-save secondary" type="button" disabled={busy} onClick={() => setActiveView('templates')}>
                  <Pencil size={15} />
                  <span>管理模板</span>
                </button>
              </div>
            </div>

            <div className="channel-form-grid">
              <div className="telegram-session-section">
                <div className="telegram-section-title">SMTP 发信</div>
                <div className="telegram-session-form">
                  <ToggleRow label="启用 SMTP 发信" checked={form.enabled} onChange={enabled => setForm({ ...form, enabled })} />
                  <TwoCol>
                    <Field label="SMTP Host" value={form.host} disabled={busy} onChange={host => setForm({ ...form, host })} />
                    <Field label="SMTP Port" value={form.port} disabled={busy} inputMode="numeric" onChange={port => setForm({ ...form, port })} />
                  </TwoCol>
                  <TwoCol>
                    <Field label="用户名" value={form.username} disabled={busy} onChange={username => setForm({ ...form, username })} />
                    <Field
                      label="密码"
                      type="password"
                      placeholder={settings?.passwordConfigured ? '已保存，留空不修改' : '请输入 SMTP 密码'}
                      value={form.password}
                      disabled={busy}
                      hint={passwordStateText(settings?.passwordConfigured, 'SMTP 密码')}
                      onChange={password => setForm({ ...form, password })}
                    />
                  </TwoCol>
                  <TwoCol>
                    <Field label="From 地址" value={form.from} disabled={busy} onChange={from => setForm({ ...form, from })} />
                    <Field label="默认主题" value={form.defaultSubject} disabled={busy} onChange={defaultSubject => setForm({ ...form, defaultSubject })} />
                  </TwoCol>
                  <div className="channel-toggle-row">
                    <ToggleRow label="SMTP Auth" checked={form.auth} onChange={auth => setForm({ ...form, auth })} />
                    <ToggleRow label="STARTTLS" checked={form.startTls} onChange={startTls => setForm({ ...form, startTls })} />
                  </div>
                </div>
              </div>

              <div className="telegram-session-section">
                <div className="telegram-section-title">IMAP 收信</div>
                <div className="telegram-session-form">
                  <ToggleRow label="启用 IMAP 收信" checked={form.inboundEnabled} onChange={inboundEnabled => setForm({ ...form, inboundEnabled })} />
                  <TwoCol>
                    <Field label="IMAP Host" value={form.imapHost} disabled={busy} onChange={imapHost => setForm({ ...form, imapHost })} />
                    <Field label="IMAP Port" value={form.imapPort} disabled={busy} inputMode="numeric" onChange={imapPort => setForm({ ...form, imapPort })} />
                  </TwoCol>
                  <TwoCol>
                    <Field label="IMAP 用户名" value={form.imapUsername} disabled={busy} onChange={imapUsername => setForm({ ...form, imapUsername })} />
                    <Field
                      label="IMAP 密码"
                      type="password"
                      placeholder={settings?.imapPasswordConfigured ? '已保存，留空不修改' : '请输入 IMAP 密码'}
                      value={form.imapPassword}
                      disabled={busy}
                      hint={passwordStateText(settings?.imapPasswordConfigured, 'IMAP 密码')}
                      onChange={imapPassword => setForm({ ...form, imapPassword })}
                    />
                  </TwoCol>
                  <TwoCol>
                    <Field label="协议" value={form.imapProtocol} disabled={busy} onChange={imapProtocol => setForm({ ...form, imapProtocol })} />
                    <Field label="文件夹" value={form.imapFolder} disabled={busy} onChange={imapFolder => setForm({ ...form, imapFolder })} />
                  </TwoCol>
                </div>
              </div>
            </div>

            <div className="telegram-action-row">
              <button className="settings-save" type="button" disabled={busy} onClick={saveEmail}>
                <Check size={15} />
                <span>{busy ? '保存中' : '保存邮箱配置'}</span>
              </button>
              <button className="settings-save secondary" type="button" disabled={busy || !settings?.inboundEnabled} onClick={pollEmail}>
                <Mail size={15} />
                <span>当前账号立即收信</span>
              </button>
            </div>

            <div className="email-help-box">
              <div className="email-help-title">邮箱配置说明</div>
              <ol>
                <li>这套配置绑定当前登录账号。切换登录账号后，需要分别维护各自的邮箱配置和模板。</li>
                <li>模板管理已经独立到“模板管理”页，便于集中维护；当前配置页只负责套用模板与保存当前邮箱账号参数。</li>
                <li>密码填写邮箱密码或邮箱服务商生成的应用专用密码。保存后页面只显示星号；再次保存时密码框留空会沿用已保存密码。</li>
                <li>修改字段后必须点击“保存邮箱配置”。只刷新页面不会自动写回服务端。</li>
                <li>“当前账号立即收信”需要先启用并保存 IMAP 收信；如果收不到邮件，先确认邮箱服务商已开启 IMAP/SMTP 权限，再查看最近收信错误信息。</li>
              </ol>
            </div>
          </div>
        )}

        {activeView === 'templates' && (
          <div className="email-workspace-panel">
            <div className="telegram-session-section">
              <div className="email-panel-header">
                <div>
                  <div className="telegram-section-title">模板管理</div>
                  <div className="telegram-section-desc">把服务商模板维护集中到这里。新增、查看、编辑都走右侧抽屉，不再把模板表单直接堆在主页面。</div>
                </div>
                <div className="email-panel-actions">
                  <button className="settings-save secondary" type="button" disabled={busy} onClick={() => openTemplateDrawer('create')}>
                    <Check size={15} />
                    <span>新增模板</span>
                  </button>
                </div>
              </div>

              {templates.length ? (
                <div className="email-template-list compact">
                  {templates.map(template => (
                    <div className="email-template-item" key={template.id}>
                      <button className="email-template-main email-template-main-button" type="button" onClick={() => openTemplateDrawer('view', template)}>
                        <div className="email-template-name">{template.name}</div>
                        <div className="email-template-meta">
                          SMTP {template.host}:{template.port} · IMAP {template.imapHost}:{template.imapPort} · {template.imapProtocol}/{template.imapFolder}
                        </div>
                      </button>
                      <div className="email-template-actions">
                        <button className="telegram-session-action secondary" type="button" disabled={busy} onClick={() => openTemplateDrawer('view', template)}>
                          <Search size={14} />
                          <span>查看</span>
                        </button>
                        <button className="telegram-session-action" type="button" disabled={busy} onClick={() => editTemplate(template)}>
                          <Pencil size={14} />
                          <span>编辑</span>
                        </button>
                        <button className="telegram-session-action danger" type="button" disabled={busy} onClick={() => deleteTemplate(template)}>
                          <Trash2 size={14} />
                          <span>删除</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="telegram-empty-state">当前账号还没有邮箱模板。点击右上角“新增模板”开始创建。</div>
              )}
            </div>
          </div>
        )}
      </section>

      {isTemplateDrawerOpen && (
        <div className="email-template-drawer-backdrop" role="dialog" aria-modal="true" aria-label="邮箱模板抽屉" onClick={resetTemplateEditor}>
          <aside className="email-template-drawer" onClick={event => event.stopPropagation()}>
            <div className="email-template-drawer-header">
              <div>
                <div className="email-template-drawer-title">
                  {drawerMode === 'create' ? '新增邮箱模板' : drawerMode === 'edit' ? '编辑邮箱模板' : '查看邮箱模板'}
                </div>
                <div className="email-template-drawer-desc">
                  {drawerMode === 'view'
                    ? '从这里查看模板详情，确认无误后可切换到编辑。'
                    : '模板会保存到后端数据库，并归属于当前登录用户。'}
                </div>
              </div>
              <button className="email-template-drawer-close" type="button" onClick={resetTemplateEditor}>关闭</button>
            </div>

            <div className="email-template-drawer-body">
              <div className="telegram-session-form">
                <TwoCol>
                  <Field label="模板名称" value={templateForm.name} disabled={busy || isTemplateDrawerReadOnly} onChange={name => setTemplateForm({ ...templateForm, name })} />
                  <div className="channel-toggle-row">
                    <ToggleRow label="SMTP Auth" checked={templateForm.auth} disabled={busy || isTemplateDrawerReadOnly} onChange={auth => setTemplateForm({ ...templateForm, auth })} />
                    <ToggleRow label="STARTTLS" checked={templateForm.startTls} disabled={busy || isTemplateDrawerReadOnly} onChange={startTls => setTemplateForm({ ...templateForm, startTls })} />
                  </div>
                </TwoCol>
                <TwoCol>
                  <Field label="SMTP Host" value={templateForm.host} disabled={busy || isTemplateDrawerReadOnly} onChange={host => setTemplateForm({ ...templateForm, host })} />
                  <Field label="SMTP Port" value={templateForm.port} disabled={busy || isTemplateDrawerReadOnly} inputMode="numeric" onChange={port => setTemplateForm({ ...templateForm, port })} />
                </TwoCol>
                <TwoCol>
                  <Field label="IMAP Host" value={templateForm.imapHost} disabled={busy || isTemplateDrawerReadOnly} onChange={imapHost => setTemplateForm({ ...templateForm, imapHost })} />
                  <Field label="IMAP Port" value={templateForm.imapPort} disabled={busy || isTemplateDrawerReadOnly} inputMode="numeric" onChange={imapPort => setTemplateForm({ ...templateForm, imapPort })} />
                </TwoCol>
                <TwoCol>
                  <Field label="协议" value={templateForm.imapProtocol} disabled={busy || isTemplateDrawerReadOnly} onChange={imapProtocol => setTemplateForm({ ...templateForm, imapProtocol })} />
                  <Field label="文件夹" value={templateForm.imapFolder} disabled={busy || isTemplateDrawerReadOnly} onChange={imapFolder => setTemplateForm({ ...templateForm, imapFolder })} />
                </TwoCol>
              </div>

              {drawerMode === 'view' && editingTemplate && (
                <div className="email-help-box compact">
                  <div className="email-help-title">模板概览</div>
                  <ol>
                    <li>模板名称：{editingTemplate.name}</li>
                    <li>SMTP：{editingTemplate.host}:{editingTemplate.port}</li>
                    <li>IMAP：{editingTemplate.imapHost}:{editingTemplate.imapPort}</li>
                  </ol>
                </div>
              )}
            </div>

            <div className="email-template-drawer-footer">
              {drawerMode === 'view' ? (
                <>
                  <button className="settings-save secondary" type="button" disabled={busy} onClick={resetTemplateEditor}>
                    <span>关闭</span>
                  </button>
                  <button className="settings-save" type="button" disabled={busy} onClick={() => setDrawerMode('edit')}>
                    <Pencil size={15} />
                    <span>编辑模板</span>
                  </button>
                </>
              ) : (
                <>
                  <button className="settings-save secondary" type="button" disabled={busy} onClick={resetTemplateEditor}>
                    <span>取消</span>
                  </button>
                  <button className="settings-save" type="button" disabled={busy} onClick={saveTemplate}>
                    <Check size={15} />
                    <span>{drawerMode === 'create' ? '创建模板' : '保存修改'}</span>
                  </button>
                </>
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

function ComingSoonChannel({ title }) {
  return (
    <section className="settings-panel">
      <div className="settings-panel-title">
        <MessageCircle size={15} />
        <span>{title}</span>
      </div>
      <div className="channel-placeholder">
        <div className="settings-row-title">{title} 接入预留</div>
        <div className="settings-row-desc">页面结构已预留，后续接入 API 后可以直接补配置表单、状态检测和 webhook 设置。</div>
      </div>
    </section>
  );
}

function ChannelSummary({ title, desc, ok, icon: Icon }) {
  return (
    <div className="channel-summary-card">
      {Icon && (
        <div className="channel-summary-icon">
          <Icon size={22} />
        </div>
      )}
      <div className="channel-summary-text">
        <div className="settings-row-title">{title}</div>
        <div className="settings-row-desc">{desc}</div>
      </div>
      <span className={`status-pill ${ok ? 'ok' : ''}`}>{ok ? '已启用' : '未启用'}</span>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', hint, ...props }) {
  return (
    <label>
      <span>{label}</span>
      <input type={type} value={value} onChange={event => onChange(event.target.value)} {...props} />
      {hint && <div className="field-hint">{hint}</div>}
    </label>
  );
}

function ToggleRow({ label, checked, onChange, disabled = false }) {
  return (
    <div className="settings-row compact-row">
      <div className="settings-row-title">{label}</div>
      <button className={`toggle-switch ${checked ? 'on' : ''}`} type="button" disabled={disabled} onClick={() => onChange(!checked)}>
        <span />
      </button>
    </div>
  );
}

function TwoCol({ children }) {
  return <div className="channel-two-col">{children}</div>;
}

function telegramStateText(state) {
  const labels = {
    WAIT_PHONE_NUMBER: '等待手机号',
    STARTING: '正在请求验证码',
    WAIT_CODE: '等待验证码',
    WAIT_PASSWORD: '等待 2FA 密码',
    READY: '已登录',
    FAILED: '失败',
    CLOSED: '已关闭',
  };
  return labels[state] || state || '未开始';
}

function whatsappPersonalStateText(state) {
  const labels = {
    STARTING: '正在启动',
    WAIT_QR: '等待扫码',
    AUTHENTICATED: '已认证',
    READY: '已登录',
    FAILED: '失败',
    CLOSED: '已关闭',
  };
  return labels[state] || state || '未开始';
}

function telegramStepText(state) {
  const labels = {
    WAIT_PHONE_NUMBER: '请输入要登录的 Telegram 用户号手机号。',
    STARTING: '正在向 Telegram 请求验证码，请稍等。',
    WAIT_CODE: '验证码已请求。请查看 Telegram App 或短信，并输入验证码。',
    WAIT_PASSWORD: '验证码已通过。该账号开启了 2FA，请输入 Telegram 二步验证密码。',
    READY: '用户号已登录，可以接收和发送 Telegram 用户号消息。',
    FAILED: '上次登录失败，可以重新提交手机号再试。',
  };
  return labels[state] || '等待下一步操作。';
}

function telegramContactName(contact) {
  const name = [contact?.firstName, contact?.lastName].filter(Boolean).join(' ').trim();
  return name || (contact?.username ? `@${contact.username}` : contact?.phone || contact?.id || 'Telegram 用户');
}
